import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { EnemyConfig, GameContent, SkillConfig, TowerConfig } from "@bastion/data";
import { assetManifest, resolveAssetUrls } from "@bastion/game-renderer";
import { useI18n } from "./i18n";

type EncyclopediaCategory = "towers" | "enemies" | "skills";
type EncyclopediaEntry = {
  id: string;
  name: string;
  subtitle: string;
  icon: string;
  skinUrl?: string;
};
type DetailRow = { label: string; value: string | number };

// 构建皮肤贴图 URL 映射（仅在浏览器环境中）
function buildSkinUrlMap(): Record<string, string> {
  if (typeof document === "undefined") return {};
  const resolved = resolveAssetUrls(assetManifest, document.baseURI);
  return Object.fromEntries(
    Object.entries(resolved).filter(([k]) => k.startsWith("tower.") || k.startsWith("enemy.")),
  );
}
const SKIN_URLS = buildSkinUrlMap();

export interface EncyclopediaModalProps {
  content: GameContent;
  onClose(): void;
}

export function EncyclopediaModal({ content, onClose }: EncyclopediaModalProps) {
  const { t, tEntity } = useI18n();
  const [category, setCategory] = useState<EncyclopediaCategory>("towers");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const entries = getEntries(category, content, tEntity, t);
  const selectedIdForCategory = entries.some((entry) => entry.id === selectedId)
    ? selectedId
    : (entries[0]?.id ?? null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const selectedTower =
    category === "towers" ? content.towers[selectedIdForCategory ?? ""] : undefined;
  const selectedEnemy =
    category === "enemies"
      ? [...Object.values(content.enemies), ...Object.values(content.bosses)].find(
          (enemy) => enemy.id === selectedIdForCategory,
        )
      : undefined;
  const selectedSkill =
    category === "skills" ? content.skills[selectedIdForCategory ?? ""] : undefined;
  const title = entries.find((entry) => entry.id === selectedIdForCategory)?.name ?? "";
  return (
    <div className="encyclopedia-backdrop" data-testid="encyclopedia-modal" onClick={onClose}>
      <section
        className="encyclopedia-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="encyclopedia-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="encyclopedia-header">
          <h2 id="encyclopedia-title">{t("encyclopedia_title")}</h2>
          <button
            className="encyclopedia-close"
            data-testid="encyclopedia-close"
            onClick={onClose}
            aria-label={t("encyclopedia_close")}
          >
            ✕
          </button>
        </header>

        <nav className="encyclopedia-tabs" aria-label={t("encyclopedia_title")}>
          {(["towers", "enemies", "skills"] as const).map((nextCategory) => (
            <button
              key={nextCategory}
              className={`encyclopedia-tab ${category === nextCategory ? "active" : ""}`}
              data-testid={`encyclopedia-tab-${nextCategory}`}
              aria-pressed={category === nextCategory}
              onClick={() => {
                setCategory(nextCategory);
                setSelectedId(null);
              }}
            >
              {t(`encyclopedia_tab_${nextCategory}`)}
            </button>
          ))}
        </nav>

        <div className="encyclopedia-body">
          <nav className="encyclopedia-list" aria-label={t(`encyclopedia_tab_${category}`)}>
            {entries.length === 0 ? (
              <p className="encyclopedia-empty">{t("encyclopedia_skill_unavailable")}</p>
            ) : (
              entries.map((entry) => (
                <button
                  key={entry.id}
                  className={`encyclopedia-entry ${selectedIdForCategory === entry.id ? "active" : ""}`}
                  data-testid={`encyclopedia-entry-${entry.id}`}
                  aria-pressed={selectedIdForCategory === entry.id}
                  onClick={() => setSelectedId(entry.id)}
                >
                  {entry.skinUrl ? (
                    <img
                      src={entry.skinUrl}
                      alt=""
                      className="encyclopedia-entry-skin"
                      aria-hidden="true"
                    />
                  ) : (
                    <span className="encyclopedia-entry-icon" aria-hidden="true">
                      {entry.icon}
                    </span>
                  )}
                  <span className="encyclopedia-entry-copy">
                    <strong>{entry.name}</strong>
                    <small>{entry.subtitle}</small>
                  </span>
                </button>
              ))
            )}
          </nav>

          <article className="encyclopedia-detail" data-testid="encyclopedia-detail">
            {!selectedIdForCategory ? (
              <p className="encyclopedia-empty">{t("encyclopedia_select_hint")}</p>
            ) : (
              <>
                <div className="encyclopedia-detail-heading">
                  <span className="encyclopedia-detail-icon" aria-hidden="true">
                    {(() => {
                      const e = entries.find((entry) => entry.id === selectedIdForCategory);
                      return e?.skinUrl ? (
                        <img src={e.skinUrl} alt="" className="encyclopedia-detail-skin" />
                      ) : (
                        e?.icon
                      );
                    })()}
                  </span>
                  <div>
                    <h3>{title}</h3>
                    <p>{entries.find((entry) => entry.id === selectedIdForCategory)?.subtitle}</p>
                  </div>
                </div>
                {selectedTower && <TowerDetails tower={selectedTower} content={content} />}
                {selectedEnemy && <EnemyDetails enemy={selectedEnemy} content={content} />}
                {selectedSkill && <SkillDetails skill={selectedSkill} />}
              </>
            )}
          </article>
        </div>
      </section>
    </div>
  );
}

function getEntries(
  category: EncyclopediaCategory,
  content: GameContent,
  tEntity: ReturnType<typeof useI18n>["tEntity"],
  t: ReturnType<typeof useI18n>["t"],
): EncyclopediaEntry[] {
  if (category === "towers") {
    return Object.values(content.towers).map((tower) => ({
      id: tower.id,
      name: tEntity("tower", tower.id),
      subtitle: tower.position
        ? tEntity("tower_position", tower.id)
        : damageTypeLabel(tower.damageType, t),
      icon: content.skills[tower.skillIds?.[0] ?? ""]?.icon ?? "🗼",
      skinUrl: SKIN_URLS[`tower.${tower.id}`],
    }));
  }
  if (category === "enemies") {
    return [...Object.values(content.enemies), ...Object.values(content.bosses)].map((enemy) => ({
      id: enemy.id,
      name: tEntity(enemy.isBoss ? "boss" : "enemy", enemy.id),
      subtitle: enemy.isBoss ? t("encyclopedia_boss") : t("encyclopedia_normal_enemy"),
      icon: enemy.isBoss ? "👑" : "👾",
      skinUrl: SKIN_URLS[`enemy.${enemy.id}`],
    }));
  }
  return Object.values(content.skills).map((skill) => ({
    id: skill.id,
    name: tEntity("skill", skill.id),
    subtitle:
      skill.type === "GLOBAL" ? t("encyclopedia_global_skill") : t("encyclopedia_tower_skill"),
    icon: skill.icon ?? (skill.type === "GLOBAL" ? "✨" : "⚡"),
  }));
}

function TowerDetails({ tower, content }: { tower: TowerConfig; content: GameContent }) {
  const { t, tEntity } = useI18n();
  const rows: DetailRow[] = [
    { label: t("stat_cost"), value: `${tower.cost} 🪙` },
    { label: t("help_tower_col_role"), value: damageTypeLabel(tower.damageType, t) },
    ...(tower.stats.maxHp > 0 ? [{ label: t("stat_hp"), value: tower.stats.maxHp }] : []),
    ...(tower.stats.attack > 0 ? [{ label: t("stat_attack"), value: tower.stats.attack }] : []),
    ...(tower.stats.magic > 0 ? [{ label: t("stat_magic"), value: tower.stats.magic }] : []),
    ...(tower.stats.defense > 0 ? [{ label: t("stat_defense"), value: tower.stats.defense }] : []),
    ...(tower.stats.magicResistance > 0
      ? [{ label: t("stat_magic_resistance"), value: tower.stats.magicResistance }]
      : []),
    ...(tower.stats.accuracy > 0
      ? [{ label: t("stat_accuracy"), value: tower.stats.accuracy }]
      : []),
    ...(tower.stats.critRate > 0
      ? [{ label: t("stat_crit_rate"), value: `${tower.stats.critRate}%` }]
      : []),
    ...(tower.stats.critDamage > 1
      ? [{ label: t("stat_crit_damage"), value: `×${tower.stats.critDamage}` }]
      : []),
    { label: t("stat_attack_speed"), value: tower.attackSpeed },
    { label: t("stat_range"), value: tower.range },
    { label: t("stat_projectile_speed"), value: tower.projectileSpeed },
    { label: t("stat_max_level"), value: tower.maxLevel },
    {
      label: t("stat_sell_refund"),
      value: t("encyclopedia_refund_rate", { percent: Math.round(tower.sellRefundRate * 100) }),
    },
  ];
  const traits: string[] = [];
  if (tower.splashRadius) traits.push(`${t("stat_splash")}: ${tower.splashRadius}`);
  if (tower.slowOnHit && tower.slowDuration) {
    traits.push(
      t("encyclopedia_slow", {
        percent: Math.round(tower.slowOnHit * 100),
        duration: tower.slowDuration,
      }),
    );
  }
  if (tower.chainCount) traits.push(`${t("stat_chain")}: ×${tower.chainCount}`);
  if (tower.dotOnHit)
    traits.push(
      t("encyclopedia_dot", {
        dps: tower.dotOnHit.dps,
        duration: tower.dotOnHit.duration,
        damageType: damageTypeLabel(tower.dotOnHit.damageType, t),
      }),
    );

  return (
    <>
      <DetailSection title={t("tower_stats_title")} rows={rows} />
      {(tower.skillIds ?? []).map((skillId) => {
        const skill = content.skills[skillId];
        if (!skill) return null;
        const condition = skill.releaseCondition;
        const releaseDescription =
          condition?.kind === "attack_count"
            ? t("encyclopedia_charge_attacks", { count: condition.count })
            : "";
        return (
          <DetailSection
            key={skillId}
            title={tEntity("skill", skillId)}
            rows={[
              ...(releaseDescription
                ? [{ label: t("encyclopedia_triggered_on_max_level"), value: releaseDescription }]
                : []),
              ...(skill.nextAttack?.kind === "ice_cone"
                ? [
                    {
                      label: t("encyclopedia_damage_multiplier"),
                      value: `×${skill.nextAttack.damageMultiplier}`,
                    },
                  ]
                : []),
              ...(skill.nextAttack?.kind === "area_damage"
                ? [
                    {
                      label: t("encyclopedia_target_damage_multiplier"),
                      value: `×${skill.nextAttack.targetDamageMultiplier}`,
                    },
                    {
                      label: t("encyclopedia_other_damage_multiplier"),
                      value: `×${skill.nextAttack.otherDamageMultiplier}`,
                    },
                  ]
                : []),
              ...(skill.nextAttack?.kind === "ice_cone"
                ? [
                    {
                      label: t("encyclopedia_projectile_speed_multiplier"),
                      value: `×${skill.nextAttack.projectileSpeedMultiplier}`,
                    },
                    {
                      label: t("encyclopedia_impact_radius"),
                      value: `${skill.nextAttack.impactRadiusCells}格`,
                    },
                    {
                      label: t("encyclopedia_duration", { seconds: skill.nextAttack.slowDuration }),
                      value: "",
                    },
                  ]
                : []),
              ...(skill.nextAttack?.kind === "area_damage"
                ? [{ label: t("stat_range"), value: skill.nextAttack.radius }]
                : []),
              ...(skill.attackSpeedFactor !== undefined
                ? [{ label: t("stat_attack_speed_boost"), value: `×${skill.attackSpeedFactor}` }]
                : []),
              ...(skill.attackSpeedDuration !== undefined
                ? [
                    {
                      label: t("encyclopedia_duration", { seconds: skill.attackSpeedDuration }),
                      value: "",
                    },
                  ]
                : []),
            ]}
          >
            {skill.skillDescription && (
              <SkillDescriptionColumns description={tEntity("tower_skill", skill.id)} />
            )}
          </DetailSection>
        );
      })}
      {traits.length > 0 && (
        <DetailSection title={t("encyclopedia_special_traits")}>
          <ul className="encyclopedia-traits">
            {traits.map((trait) => (
              <li key={trait}>{trait}</li>
            ))}
          </ul>
        </DetailSection>
      )}
      {tower.upgrades.length > 0 && (
        <DetailSection title={t("encyclopedia_upgrades")}>
          <div className="encyclopedia-upgrades">
            {tower.upgrades.map((upgrade) => (
              <div className="encyclopedia-upgrade" key={upgrade.level}>
                <strong>{t("encyclopedia_level", { level: upgrade.level })}</strong>
                <div className="encyclopedia-upgrade-values">
                  {upgrade.attack !== undefined && (
                    <span>
                      {t(tower.damageType === "magic" ? "stat_magic" : "stat_attack")}:{" "}
                      {upgrade.attack}
                    </span>
                  )}
                  {upgrade.attackSpeed !== undefined && (
                    <span>
                      {t("stat_attack_speed")}: {upgrade.attackSpeed}
                    </span>
                  )}
                  {upgrade.range !== undefined && (
                    <span>
                      {t("stat_range")}: {upgrade.range}
                    </span>
                  )}
                  {upgrade.upgradeCost !== undefined && (
                    <span>{t("build_upgrade", { cost: upgrade.upgradeCost })}</span>
                  )}
                  {upgrade.splashRadius !== undefined && (
                    <span>
                      {t("stat_splash")}: {upgrade.splashRadius}
                    </span>
                  )}
                  {upgrade.chainCount !== undefined && (
                    <span>
                      {t("stat_chain")}: {upgrade.chainCount}
                    </span>
                  )}
                  {upgrade.dotOnHit && (
                    <span>
                      {t("encyclopedia_dot", {
                        dps: upgrade.dotOnHit.dps,
                        duration: upgrade.dotOnHit.duration,
                        damageType: damageTypeLabel(upgrade.dotOnHit.damageType, t),
                      })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DetailSection>
      )}
    </>
  );
}

function EnemyDetails({ enemy, content }: { enemy: EnemyConfig; content: GameContent }) {
  const { t, tEntity } = useI18n();
  const rows: DetailRow[] = [
    { label: t("stat_hp"), value: enemy.stats.maxHp },
    { label: t("stat_speed"), value: enemy.stats.speed },
    ...(enemy.stats.attack > 0 ? [{ label: t("stat_attack"), value: enemy.stats.attack }] : []),
    ...(enemy.stats.magic > 0 ? [{ label: t("stat_magic"), value: enemy.stats.magic }] : []),
    ...(enemy.stats.defense > 0 ? [{ label: t("stat_defense"), value: enemy.stats.defense }] : []),
    ...(enemy.stats.magicResistance > 0
      ? [{ label: t("stat_magic_resistance"), value: enemy.stats.magicResistance }]
      : []),
    ...(enemy.stats.accuracy > 0
      ? [{ label: t("stat_accuracy"), value: enemy.stats.accuracy }]
      : []),
    ...(enemy.stats.resistance > 0
      ? [{ label: t("stat_resistance"), value: enemy.stats.resistance }]
      : []),
    ...(enemy.stats.critRate > 0
      ? [{ label: t("encyclopedia_crit_rate_short", { rate: enemy.stats.critRate }), value: "" }]
      : []),
    ...(enemy.stats.critDamage > 1
      ? [
          {
            label: t("encyclopedia_crit_damage_short", { damage: enemy.stats.critDamage }),
            value: "",
          },
        ]
      : []),
    { label: t("encyclopedia_gold_reward"), value: enemy.goldReward },
    { label: t("encyclopedia_lives_damage"), value: enemy.livesDamage },
  ];
  const traits: string[] = [];
  if (enemy.dodgeChance)
    traits.push(t("encyclopedia_dodge", { percent: Math.round(enemy.dodgeChance * 100) }));
  if (enemy.regenInterval && enemy.regenPercentPerTick) {
    traits.push(
      t("encyclopedia_regeneration", {
        interval: enemy.regenInterval,
        percent: Math.round(enemy.regenPercentPerTick * 100),
      }),
    );
  }
  if (enemy.splitOnDeath && enemy.splitInto) {
    const splitInto = content.enemies[enemy.splitInto] ?? content.bosses[enemy.splitInto];
    traits.push(
      t("encyclopedia_split", {
        count: enemy.splitOnDeath,
        enemy: splitInto
          ? tEntity(splitInto.isBoss ? "boss" : "enemy", splitInto.id)
          : enemy.splitInto,
      }),
    );
  }
  if (enemy.special) traits.push(enemy.special);

  return (
    <>
      <div className="encyclopedia-critical-pair" data-testid="encyclopedia-critical-pair">
        {enemy.stats.critRate > 0 && (
          <span>{t("encyclopedia_crit_rate_short", { rate: enemy.stats.critRate })}</span>
        )}
        {enemy.stats.critDamage > 1 && (
          <span>{t("encyclopedia_crit_damage_short", { damage: enemy.stats.critDamage })}</span>
        )}
      </div>
      <DetailSection title={t("enemy_stats_title")} rows={rows} />
      {(enemy.skillIds ?? []).map((skillId) => {
        const skill = content.skills[skillId];
        if (!skill || skill.type !== "MONSTER") return null;
        const condition = skill.releaseCondition;
        return (
          <DetailSection
            key={skillId}
            title={tEntity("skill", skillId)}
            rows={[
              ...(condition?.kind === "interval"
                ? [
                    {
                      label: t("encyclopedia_interval", { seconds: condition.seconds }),
                      value: "",
                    },
                    ...(condition.initialDelay !== undefined
                      ? [
                          {
                            label: t("encyclopedia_initial_delay", {
                              seconds: condition.initialDelay,
                            }),
                            value: "",
                          },
                        ]
                      : []),
                  ]
                : []),
              ...(skill.radius !== undefined
                ? [{ label: t("stat_range"), value: skill.radius }]
                : []),
              ...(skill.slowFactor !== undefined
                ? [
                    {
                      label: t("encyclopedia_slow_factor", {
                        percent: Math.round(skill.slowFactor * 100),
                      }),
                      value: "",
                    },
                  ]
                : []),
              ...(skill.slowDuration !== undefined
                ? [
                    {
                      label: t("encyclopedia_duration", { seconds: skill.slowDuration }),
                      value: "",
                    },
                  ]
                : []),
            ]}
          />
        );
      })}
      {traits.length > 0 && (
        <DetailSection title={t("encyclopedia_special_traits")}>
          <ul className="encyclopedia-traits">
            {traits.map((trait) => (
              <li key={trait}>{trait}</li>
            ))}
          </ul>
        </DetailSection>
      )}
    </>
  );
}

function SkillDetails({ skill }: { skill: SkillConfig }) {
  const { t } = useI18n();
  const rows: DetailRow[] = [
    {
      label: t("stat_cost"),
      value: skill.type === "GLOBAL" ? `${skill.cost} 🪙` : t("encyclopedia_skill_charge_info"),
    },
    ...(skill.type === "GLOBAL"
      ? [{ label: t("encyclopedia_cooldown"), value: `${skill.cooldown}s` }]
      : []),
    ...(skill.damage !== undefined
      ? [
          {
            label: t("stat_damage"),
            value: `${skill.damage}${skill.damageType ? ` ${damageTypeLabel(skill.damageType, t)}` : ""}`,
          },
        ]
      : []),
    ...(skill.radius !== undefined && skill.radius > 0
      ? [{ label: t("stat_range"), value: skill.radius }]
      : []),
    ...(skill.slowFactor !== undefined
      ? [{ label: t("encyclopedia_slow_factor"), value: `${Math.round(skill.slowFactor * 100)}%` }]
      : []),
    ...(skill.slowDuration !== undefined
      ? [{ label: t("encyclopedia_duration", { seconds: skill.slowDuration }), value: "" }]
      : []),
    ...(skill.stunDuration !== undefined
      ? [{ label: t("encyclopedia_stun"), value: `${skill.stunDuration}s` }]
      : []),
    ...(skill.healLives !== undefined
      ? [{ label: t("encyclopedia_heal_lives"), value: skill.healLives }]
      : []),
    ...(skill.attackSpeedFactor !== undefined
      ? [{ label: t("stat_attack_speed_boost"), value: `×${skill.attackSpeedFactor}` }]
      : []),
    ...(skill.attackSpeedDuration !== undefined
      ? [{ label: t("encyclopedia_duration", { seconds: skill.attackSpeedDuration }), value: "" }]
      : []),
    ...(skill.control
      ? [
          {
            label: t(
              `encyclopedia_control_${skill.control.type.toLowerCase() as "slow" | "stun" | "root" | "silence"}`,
            ),
            value: `${skill.control.duration}s (${Math.round(skill.control.chance * 100)}%)`,
          },
        ]
      : []),
  ];
  return <DetailSection title={t("skill_panel_title")} rows={rows} />;
}

function SkillDescriptionColumns({ description }: { description: string }) {
  return (
    <div className="encyclopedia-skill-columns">
      <p className="encyclopedia-description">{description}</p>
      <div className="encyclopedia-skill-placeholder" aria-hidden="true" />
    </div>
  );
}

function DetailSection({
  title,
  rows,
  children,
}: {
  title: string;
  rows?: DetailRow[];
  children?: ReactNode;
}) {
  return (
    <section className="encyclopedia-detail-section">
      <h4>{title}</h4>
      {rows && (
        <dl className="encyclopedia-stats">
          {rows.map((row, index) => (
            <div className="encyclopedia-stat" key={`${row.label}-${index}`}>
              <dt>{row.label}</dt>
              {row.value !== "" && <dd>{row.value}</dd>}
            </div>
          ))}
        </dl>
      )}
      {children}
    </section>
  );
}

function damageTypeLabel(type: string, t: ReturnType<typeof useI18n>["t"]): string {
  switch (type) {
    case "physical":
      return t("help_damage_type_physical");
    case "magic":
      return t("help_damage_type_magic");
    case "fire":
      return t("help_damage_type_fire");
    case "frost":
      return t("help_damage_type_frost");
    case "poison":
      return t("help_damage_type_poison");
    default:
      return type;
  }
}

/**
 * 炮台与敌人的 L3 单体有效 DPS 对照表。
 * 伤害包含护甲/魔抗减免和期望暴击，不包含技能、范围、连锁等多目标收益。
 */
export function DamageMatrixModal({ content, onClose }: EncyclopediaModalProps) {
  const { t, tEntity } = useI18n();
  const towers = Object.values(content.towers);
  const enemies = [...Object.values(content.enemies), ...Object.values(content.bosses)];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="encyclopedia-backdrop" data-testid="damage-matrix-modal" onClick={onClose}>
      <section
        className="encyclopedia-modal damage-matrix-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="damage-matrix-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="encyclopedia-header">
          <h2 id="damage-matrix-title">{t("encyclopedia_damage_matrix_title")}</h2>
          <button
            className="encyclopedia-close"
            data-testid="damage-matrix-close"
            onClick={onClose}
            aria-label={t("encyclopedia_close")}
          >
            ✕
          </button>
        </header>
        <p className="damage-matrix-note">{t("encyclopedia_damage_matrix_note")}</p>
        <div className="damage-matrix-scroll">
          <table className="damage-matrix-table" data-testid="damage-matrix-table">
            <thead>
              <tr>
                <th className="dm-enemy">{t("encyclopedia_damage_matrix_enemy")}</th>
                {towers.map((tower) => {
                  const imageUrl = SKIN_URLS[`tower.${tower.id}`];
                  const tooltip = getTowerHoverText(tower, content, t, tEntity);
                  return (
                    <MatrixHoverHeader
                      key={tower.id}
                      className="dm-tower"
                      tooltip={tooltip}
                      dataTestId={`damage-matrix-tower-${tower.id}`}
                    >
                      <div className="dm-entity-art" data-image-failed={!imageUrl}>
                        {imageUrl && (
                          <img
                            className="dm-entity-image dm-tower-image"
                            src={imageUrl}
                            alt=""
                            aria-hidden="true"
                            onError={(event) => {
                              event.currentTarget.parentElement?.setAttribute(
                                "data-image-failed",
                                "true",
                              );
                            }}
                          />
                        )}
                        <span className="dm-entity-fallback" aria-hidden="true">
                          {content.skills[tower.skillIds?.[0] ?? ""]?.icon ?? "🗼"}
                        </span>
                      </div>
                      <span className="dm-entity-name">{tEntity("tower", tower.id)}</span>
                      <small>
                        {tower.damageType === "magic"
                          ? t("help_damage_type_magic")
                          : t("help_damage_type_physical")}
                      </small>
                    </MatrixHoverHeader>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {enemies.map((enemy) => {
                const imageUrl = SKIN_URLS[`enemy.${enemy.id}`];
                const tooltip = getEnemyHoverText(enemy, t, tEntity);
                return (
                  <tr key={enemy.id}>
                    <MatrixHoverHeader
                      className="dm-enemy"
                      tooltip={tooltip}
                      dataTestId={`damage-matrix-enemy-${enemy.id}`}
                    >
                      <div className="dm-enemy-identity">
                        <div className="dm-entity-art dm-enemy-art" data-image-failed={!imageUrl}>
                          {imageUrl && (
                            <img
                              className="dm-entity-image dm-enemy-image"
                              src={imageUrl}
                              alt=""
                              aria-hidden="true"
                              onError={(event) => {
                                event.currentTarget.parentElement?.setAttribute(
                                  "data-image-failed",
                                  "true",
                                );
                              }}
                            />
                          )}
                          <span className="dm-entity-fallback" aria-hidden="true">
                            {enemy.isBoss ? "👑" : "👾"}
                          </span>
                        </div>
                        <span className="dm-entity-name">
                          {tEntity(enemy.isBoss ? "boss" : "enemy", enemy.id)}
                          {enemy.isBoss && <small>{t("encyclopedia_boss")}</small>}
                        </span>
                      </div>
                    </MatrixHoverHeader>
                    {towers.map((tower) => (
                      <td className="dm-cell" key={tower.id}>
                        {getEffectiveDps(tower, enemy).toFixed(1)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MatrixHoverHeader({
  className,
  tooltip,
  dataTestId,
  children,
}: {
  className: string;
  tooltip: string;
  dataTestId: string;
  children: ReactNode;
}) {
  const headerRef = useRef<HTMLTableCellElement>(null);
  const tooltipId = `matrix-tooltip-${dataTestId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    placement: "below" as "above" | "below",
  });

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rect = headerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const placement = rect.top > 245 ? "above" : "below";
      setPosition({
        left: Math.max(170, Math.min(rect.left + rect.width / 2, window.innerWidth - 170)),
        top: placement === "above" ? rect.top - 10 : rect.bottom + 10,
        placement,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  return (
    <th
      ref={headerRef}
      className={`${className} matrix-hover-trigger`}
      data-testid={dataTestId}
      tabIndex={0}
      aria-describedby={isOpen ? tooltipId : undefined}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
    >
      {children}
      {isOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            id={tooltipId}
            className="matrix-hover-tooltip"
            role="tooltip"
            data-placement={position.placement}
            data-testid={`${dataTestId}-tooltip`}
            style={{ left: position.left, top: position.top }}
          >
            <strong className="matrix-hover-tooltip-title">{tooltip.split("\n")[0]}</strong>
            <div className="matrix-hover-tooltip-divider" />
            <div className="matrix-hover-tooltip-stats">
              {tooltip
                .split("\n")
                .slice(1)
                .map((line, index) => (
                  <span
                    className={line.startsWith("  ") ? "matrix-hover-tooltip-detail" : ""}
                    key={`${line}-${index}`}
                  >
                    {line.trim()}
                  </span>
                ))}
            </div>
          </div>,
          document.body,
        )}
    </th>
  );
}

function getEffectiveDps(tower: TowerConfig, enemy: EnemyConfig): number {
  const upgrade = tower.upgrades.find((item) => item.level === tower.maxLevel) ?? tower.upgrades[0];
  const damageType = tower.damageType;
  const baseDamage =
    upgrade.attack ?? (damageType === "magic" ? tower.stats.magic : tower.stats.attack);
  const attackSpeed = upgrade.attackSpeed ?? tower.stats.attackSpeed;
  const resistance = damageType === "magic" ? enemy.stats.magicResistance : enemy.stats.defense;
  const mitigatedDamage = (baseDamage * 100) / (100 + resistance);
  const critRate = tower.stats.critRate / 100;
  const expectedDamagePerHit =
    (1 - critRate) * Math.max(1, Math.floor(mitigatedDamage)) +
    critRate * Math.max(1, Math.floor(mitigatedDamage * tower.stats.critDamage));

  return expectedDamagePerHit * attackSpeed;
}

function getTowerHoverText(
  tower: TowerConfig,
  content: GameContent,
  t: ReturnType<typeof useI18n>["t"],
  tEntity: ReturnType<typeof useI18n>["tEntity"],
): string {
  const upgrade = tower.upgrades.find((item) => item.level === tower.maxLevel) ?? tower.upgrades[0];
  const attack =
    upgrade.attack ?? (tower.damageType === "magic" ? tower.stats.magic : tower.stats.attack);
  const attackSpeed = upgrade.attackSpeed ?? tower.stats.attackSpeed;
  const range = upgrade.range ?? tower.range;
  const lines = [
    `${t("encyclopedia_level", { level: tower.maxLevel })} · ${tEntity("tower", tower.id)}`,
    `${tower.damageType === "magic" ? t("stat_magic") : t("stat_attack")}: ${attack}`,
    `${t("stat_attack_speed")}: ${attackSpeed}/s`,
    `${t("stat_range")}: ${range}`,
    `${t("stat_crit_rate")}: ${tower.stats.critRate}% · ${t("stat_crit_damage")}: ×${tower.stats.critDamage}`,
  ];

  const splashRadius = upgrade.splashRadius ?? tower.splashRadius;
  const chainCount = upgrade.chainCount ?? tower.chainCount;
  const slowOnHit = upgrade.slowOnHit ?? tower.slowOnHit;
  if (splashRadius) lines.push(`${t("stat_splash")}: ${splashRadius}`);
  if (chainCount) lines.push(`${t("stat_chain")}: ${chainCount}`);
  if (slowOnHit) lines.push(`${t("stat_slow")}: ${Math.round(slowOnHit * 100)}%`);

  for (const skillId of tower.skillIds ?? []) {
    const skill = content.skills[skillId];
    if (!skill) continue;
    const condition = skill.releaseCondition;
    const charge =
      condition?.kind === "attack_count"
        ? t("encyclopedia_charge_attacks", { count: condition.count })
        : "";
    lines.push(`${tEntity("skill", skillId)}${charge ? ` · ${charge}` : ""}`);
    if (skill.nextAttack?.kind === "ice_cone") {
      lines.push(
        `  ${t("encyclopedia_damage_multiplier", { value: skill.nextAttack.damageMultiplier })}`,
      );
      lines.push(
        `  ${t("encyclopedia_impact_radius", { value: skill.nextAttack.impactRadiusCells })}`,
      );
      lines.push(
        `  ${t("encyclopedia_slow_factor", { percent: Math.round(skill.nextAttack.slowFactor * 100) })}`,
      );
    } else if (skill.nextAttack?.kind === "area_damage") {
      lines.push(
        `  ${t("encyclopedia_target_damage_multiplier", { value: skill.nextAttack.targetDamageMultiplier })}`,
      );
      lines.push(
        `  ${t("encyclopedia_other_damage_multiplier", { value: skill.nextAttack.otherDamageMultiplier })}`,
      );
      lines.push(`  ${t("stat_range")}: ${skill.nextAttack.radius}`);
    }
    if (skill.autoSkill) {
      lines.push(
        `  ${t("encyclopedia_damage_multiplier", { value: skill.autoSkill.damageMultiplier })}`,
      );
      lines.push(`  ${t("stat_range")}: ${skill.autoSkill.radius}`);
    }
    if (skill.attackSpeedFactor !== undefined) {
      lines.push(
        `  ${t("stat_attack_speed_boost")}: ×${skill.attackSpeedFactor} (${skill.attackSpeedDuration ?? 0}s)`,
      );
    }
  }

  return lines.join("\n");
}

function getEnemyHoverText(
  enemy: EnemyConfig,
  t: ReturnType<typeof useI18n>["t"],
  tEntity: ReturnType<typeof useI18n>["tEntity"],
): string {
  const lines = [
    `${tEntity(enemy.isBoss ? "boss" : "enemy", enemy.id)}${enemy.isBoss ? ` · ${t("encyclopedia_boss")}` : ""}`,
    `${t("stat_hp")}: ${enemy.stats.maxHp}`,
    `${t("stat_speed")}: ${enemy.stats.speed}`,
    `${t("stat_defense")}: ${enemy.stats.defense}`,
    `${t("stat_magic_resistance")}: ${enemy.stats.magicResistance}`,
    `${t("encyclopedia_lives_damage")}: ${enemy.livesDamage}`,
    `${t("encyclopedia_gold_reward")}: ${enemy.goldReward}`,
  ];
  if (enemy.dodgeChance)
    lines.push(`${t("encyclopedia_dodge", { percent: Math.round(enemy.dodgeChance * 100) })}`);
  if (enemy.regenInterval && enemy.regenPercentPerTick) {
    lines.push(
      t("encyclopedia_regeneration", {
        interval: enemy.regenInterval,
        percent: Math.round(enemy.regenPercentPerTick * 100),
      }),
    );
  }
  if (enemy.splitOnDeath && enemy.splitInto) {
    lines.push(
      t("encyclopedia_split", {
        count: enemy.splitOnDeath,
        enemy: tEntity("enemy", enemy.splitInto),
      }),
    );
  }
  return lines.join("\n");
}
